import Drawer, { DrawerProps } from '@mui/material/Drawer';
import { IconX } from '@tabler/icons-react';
import { IconButton, Skeleton, Stack, SvgIcon, Typography } from '../material';
import { borderRadius } from '../theme';

export const detailDrawerWidths = {
  small: '450px',
  medium: '800px',
  large: '1200px',
} as const;

export type DetailDrawerSize = keyof typeof detailDrawerWidths;

export type DetailDrawerProps = Omit<
  DrawerProps,
  'title' | 'PaperProps' | 'slotProps'
> & {
  title: string | undefined;
  description?: string;
  titleIcon?: React.ReactNode;
  size?: DetailDrawerSize;
};

export const DetailDrawer = ({
  title,
  description,
  titleIcon,
  size = 'medium',
  children,
  open,
  onClose,
  ...restDrawerProps
}: DetailDrawerProps) => (
  <Drawer
    {...restDrawerProps}
    open={open}
    onClose={onClose}
    // `Drawer` forwards this to the paper, so the paper slot stays untouched.
    elevation={0}
    slotProps={{
      paper: {
        elevation: 0,
        sx: {
          borderTopLeftRadius: borderRadius.abs(8, 'l'),
          borderBottomLeftRadius: borderRadius.abs(8, 'l'),
          // drawer should never be wider than the viewport width.
          width: `min(${detailDrawerWidths[size]},100%)`,
        },
      },
    }}
  >
    <Stack
      direction="row"
      spacing={2}
      sx={{
        backgroundColor: t => t.palette.elevation[4].background,
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        py: 2,
      }}
      useFlexGap
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', minWidth: 0 }}
        useFlexGap
      >
        {titleIcon}
        <Stack sx={{ minWidth: 0 }}>
          {title ? (
            <Typography variant="h5" noWrap>
              {title}
            </Typography>
          ) : (
            <Skeleton width={300} height={26} variant="text" />
          )}
          {description ? (
            <Typography variant="body1" color="textSecondary" noWrap>
              {description}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <IconButton
        color="inherit"
        onClick={e => onClose?.(e, 'backdropClick')}
        data-cy="detail-drawer-close"
      >
        <SvgIcon>
          <IconX />
        </SvgIcon>
      </IconButton>
    </Stack>

    <Stack direction="column" sx={{ overflowY: 'auto', height: 1 }}>
      {children}
    </Stack>
  </Drawer>
);
